import { isAxiosError } from 'axios';
import React, { useState } from 'react';

import { UserDisplayNameEntity } from 'Models/Entities';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { TextArea } from 'Views/Components/New/TextArea';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { Flex } from 'Views/Components/Flex/Flex';
import { useCreateUserTypeRequest } from '../Hooks/useCreateUserTypeRequest';
import { getUserTypeRequestText, UserTypeRequestTextName } from '../Utils/userTypeRequestText';

const MESSAGE_MAX_LENGTH = 1000;

interface RequestUserTypeModalProps {
	/** The customer the request is about. Omit when the logged-in user requests for themselves. */
	userId?: string;
	requestableTypes: UserDisplayNameEntity[];
	isOpen: boolean;
	onClose: () => void;
}

/**
 * Lets a customer (or staff on their behalf) request one of the requestable account types.
 * Submitting notifies the type's configured receivers; staff fulfil the request manually.
 */
export function RequestUserTypeModal({ userId, requestableTypes, isOpen, onClose }: RequestUserTypeModalProps) {
	const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
	const [message, setMessage] = useState('');
	const [error, setError] = useState<string | null>(null);
	const mutation = useCreateUserTypeRequest(userId);

	const options = requestableTypes.map(t => ({ value: t.id, label: t.displayName }));
	const selectedOption = options.find(o => o.value === selectedTypeId) ?? null;
	const selectedType = requestableTypes.find(t => t.id === selectedTypeId);
	// Wording follows the selected type, so picking one can reword the modal for that type.
	const text = (name: UserTypeRequestTextName) => getUserTypeRequestText(name, selectedType);

	const handleClose = () => {
		if (mutation.isPending) return;
		setSelectedTypeId(null);
		setMessage('');
		setError(null);
		onClose();
	};

	const onConfirm = async () => {
		if (!selectedTypeId || mutation.isPending) {
			return;
		}
		setError(null);
		try {
			await mutation.mutateAsync({
				userId,
				userDisplayNameId: selectedTypeId,
				message: message.trim() || undefined,
			});
			handleClose();
		} catch (e) {
			const hasServerMessage =
				isAxiosError(e) && typeof e.response?.data === 'string' && e.response.data.length > 0;
			const responseMessage = hasServerMessage ? (e as any).response.data : text('sendFailed');
			setError(responseMessage);
		}
	};

	return (
		<NewModal isOpen={isOpen} onRequestClose={handleClose} label={text('title')} modalSize="sm">
			<NewModal.Body>
				<Flex direction="col" gap="sm">
					<div data-test-id="request-user-type-select">
						<SelectInput
							label={text('typeLabel')}
							required
							placeholder={text('typePlaceholder')}
							options={options as any}
							value={selectedOption}
							onChange={(option: any) => setSelectedTypeId(option?.value ?? null)}
							renderOption={(option: any) => {
								const type = requestableTypes.find(t => t.id === option.value);
								return (
									<span
										className="inline-flex items-center gap-2"
										data-test-id={`request-user-type-option-${option.value}`}
									>
										{type?.tagColour && (
											<span
												className="inline-block w-3 h-3 rounded-full border shrink-0"
												style={{ backgroundColor: type.tagColour, borderColor: '#e5e7eb' }}
											/>
										)}
										{option.label}
									</span>
								);
							}}
						/>
					</div>
					{selectedType?.description && <p className="!text-xs text-slate-500">{selectedType.description}</p>}
					<TextArea
						label={text('messageLabel')}
						placeholder={text('messagePlaceholder')}
						maxLength={MESSAGE_MAX_LENGTH}
						value={message}
						onChange={e => setMessage(e.target.value)}
						data-test-id="request-user-type-message"
					/>
					{error && (
						<p className="!text-xs text-red-600" data-test-id="request-user-type-error">
							{error}
						</p>
					)}
				</Flex>
			</NewModal.Body>
			<NewModal.Footer>
				<div className="flex flex-col w-full gap-2">
					<Button
						display={Display.Solid}
						colors={Colors.Primary}
						newStyles
						onClick={onConfirm}
						disabled={!selectedTypeId || mutation.isPending}
						className="w-full"
						buttonProps={{ 'data-test-id': 'request-user-type-confirm' } as any}
					>
						{mutation.isPending ? text('submitPending') : text('submit')}
					</Button>
					<Button
						display={Display.Outline}
						colors={Colors.Black}
						newStyles
						onClick={handleClose}
						disabled={mutation.isPending}
						className="w-full"
					>
						{text('cancel')}
					</Button>
				</div>
			</NewModal.Footer>
		</NewModal>
	);
}
